import { observer } from 'mobx-react-lite'
import { FloatingSaveShadcnComponent } from 'app/components/floating-save/FloatingSaveShadcnComponent'
import { useFloatingSave } from 'app/components/floating-save/useFloatingSave'
import { SmartForm } from 'app/components/form/SmartForm'
import { Label } from 'app/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Switch } from 'app/components/ui/switch'
import { commLogModeLabel } from 'app/lib/commLog'
import {
  PMS_COMM_LOG_CHANNEL_MODES,
  PMS_COMM_LOG_CHANNELS,
  PMS_COMM_LOG_DO_NOT_SEND,
  PMS_COMM_LOG_GROUP_LABELS,
  PMS_COMM_LOG_GROUPS,
  PMS_COMM_LOG_RULES,
  type PmsCommLogChannel,
} from 'app/lib/pmsCommLog'
import { commLogWritebackValidator } from 'app/components/pms-comm-log/commLogWritebackValidator'
import type { UsePmsCommLogReturn } from 'app/components/pms-comm-log/usePmsCommLog'

export const PmsCommLogComponent = observer(function PmsCommLogComponent({
  isKollaLocation,
  pmsName,
  enabled,
  toggleEnabled,
  channels,
  setChannel,
  providerOptions,
  provider,
  setProvider,
  isDirty,
  saveError,
  onSubmit,
}: UsePmsCommLogReturn) {
  const floatingSave = useFloatingSave({ isDirtyForm: isDirty, handleSaveConfiguration: onSubmit })

  if (!isKollaLocation) {
    return (
      <div className="space-y-2">
        <h2 className="text-label-md">Comm Log Writeback</h2>
        <p className="text-body-sm text-subtle">
          Writeback is not available for {pmsName} yet. It works today for practices connected through Kolla.
        </p>
      </div>
    )
  }

  return (
    <SmartForm
      id="comm-log-writeback-settings"
      className="max-w-none"
      validator={commLogWritebackValidator}
      method="post"
      hideButtons
      preventSubmit
      onSubmit={onSubmit}
    >
      <div className="col-span-6 space-y-1">
        <h2 className="text-label-md">Comm Log Writeback</h2>
        <p className="text-body-sm text-subtle">Entries Trackable adds to the patient chart in {pmsName}.</p>
      </div>

      <div className="col-span-6 flex items-center gap-x-3">
        <Switch id="comm-log-writeback" checked={enabled} onCheckedChange={toggleEnabled} />
        <Label htmlFor="comm-log-writeback">Write Trackable activity to the comm log</Label>
      </div>

      <div className="col-span-6 flex items-center gap-x-4">
        <div className="flex-1">
          <Label htmlFor="comm-log-writeback-provider">File entries under</Label>
          <div className="text-body-xs text-subtle">The {pmsName} provider each entry is recorded against</div>
        </div>
        <Select value={provider ?? ``} disabled={!enabled} onValueChange={setProvider}>
          <SelectTrigger
            id="comm-log-writeback-provider"
            className="w-56 text-ui-xs"
            data-testid="comm-log-writeback-provider"
          >
            <SelectValue placeholder="Choose a provider" />
          </SelectTrigger>
          <SelectContent>
            {providerOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {PMS_COMM_LOG_GROUPS.map((group) => (
        <section key={group} className="col-span-6 space-y-2">
          <h3 className="text-label-sm text-subtle">{PMS_COMM_LOG_GROUP_LABELS[group]}</h3>
          <ul className="divide-y divide-default rounded-lg border border-default bg-surface">
            {PMS_COMM_LOG_RULES.filter((rule) => rule.group === group).map((rule) => (
              <li key={rule.key} className="flex items-center gap-x-4 px-4 py-3">
                <div className="flex-1">
                  <div className="text-body-sm">{rule.label}</div>
                  <div className="text-body-xs text-subtle">{rule.description}</div>
                </div>
                <span className="text-body-xs text-minimal">Write as</span>
                <Select
                  value={channels[rule.key] ?? PMS_COMM_LOG_DO_NOT_SEND}
                  disabled={!enabled}
                  onValueChange={(value) =>
                    setChannel(rule.key, value === PMS_COMM_LOG_DO_NOT_SEND ? undefined : (value as PmsCommLogChannel))
                  }
                >
                  <SelectTrigger className="w-40 text-ui-xs" aria-label={`${rule.label} channel`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PMS_COMM_LOG_DO_NOT_SEND}>Do not send</SelectItem>
                    {PMS_COMM_LOG_CHANNELS.map((channel) => (
                      <SelectItem key={channel} value={channel}>
                        {commLogModeLabel(PMS_COMM_LOG_CHANNEL_MODES[channel])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {saveError && (
        <p className="col-span-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-body-sm text-red-700">
          {saveError}
        </p>
      )}

      <FloatingSaveShadcnComponent {...floatingSave} buttonLabel="Save Changes" />
    </SmartForm>
  )
})
